import { Flex, TextInput, Button, Text } from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import cn from "classnames";
import { useCallback } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate, Form } from "react-router-dom";
import { FormField } from "src/components/FormField";
import { appConfig } from "src/config";
import { useRedirectUrl } from "src/hooks";
import { useTranslate } from "src/i18n";
import { RoutesPaths } from "src/routes/RoutesPaths";
import { registrationRequest } from "src/store/fetchers/registrationRequest";

import { PasswordField } from "./components/PasswordField";
import { PhoneInput } from "./components/PhoneInput";
import css from "./RegistrationPage.module.scss";
import { RegFormInput } from "./RegistrationPage.types";

export const RegistrationPage = () => {
  const { t } = useTranslate();
  const { forwardRedirectUrl } = useRedirectUrl();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    trigger,
  } = useForm<RegFormInput>({
    mode: "onChange",
  });

  const [isLoading] = useAtom(registrationRequest.isLoadingAtom);
  const registration = useAction(registrationRequest);

  const onSubmit: SubmitHandler<RegFormInput> = useCallback(
    async (data) => {
      delete data.password_repeat;
      const phone = data.phone.replace(/[^\d+]/g, "");
      await registration({ ...data, phone });
    },
    [registration],
  );

  const password = watch("password", "");

  return (
    <Flex className={cn(css.main, css.full)}>
      <Flex className={cn(css.main, css.mainContainer)}>
        <Flex className={css.main}>
          <appConfig.logo
            height={50}
            onClick={() => navigate(RoutesPaths.LANDING)}
            className={css.footerLogo}
          />
          <Text variant="display-1">Регистрация</Text>
        </Flex>
        <Form className={css.regForm} onSubmit={handleSubmit(onSubmit)}>
          <FormField labelKey="registration.field.email" labelDefault="Почта">
            <TextInput
              placeholder="you@example.com"
              {...register("email", {
                required: "Обязательное поле",
                pattern: {
                  value: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
                  message: "Некорректный email",
                },
              })}
              size="xl"
              type="email"
              validationState={errors.email ? "invalid" : undefined}
              errorMessage={errors.email?.message}
              errorPlacement="outside"
            />
          </FormField>
          <FormField labelKey="registration.field.name" labelDefault="Логин">
            <TextInput
              placeholder={t(
                "registration.placeholder.name",
                "латиница, цифры, _ или -",
              )}
              {...register("name", {
                required: "Обязательное поле",
                pattern: {
                  value: /^[a-z0-9_-]+$/,
                  message: "Некорректное имя пользователя! a-z, 0-9, _-",
                },
                minLength: {
                  value: 4,
                  message: "Минимальная длина 4",
                },
                maxLength: {
                  value: 60,
                  message: "Максимальная длина 60",
                },
              })}
              size="xl"
              type="text"
              validationState={errors.name ? "invalid" : undefined}
              errorMessage={errors.name?.message}
              errorPlacement="outside"
            />
          </FormField>
          <FormField labelKey="registration.field.phone" labelDefault="Телефон">
            <PhoneInput
              control={control}
              name="phone"
              errorMessage="Неверный формат номера телефона"
              required
            />
          </FormField>
          <FormField
            labelKey="registration.field.password"
            labelDefault="Пароль"
          >
            <PasswordField
              name="password"
              control={control}
              onChange={() => trigger("password_repeat")}
            />
          </FormField>
          <FormField
            labelKey="registration.field.password_repeat"
            labelDefault="Повторите пароль"
          >
            <TextInput
              placeholder={t(
                "registration.placeholder.password_repeat",
                "тот же пароль",
              )}
              {...register("password_repeat", {
                required: "Обязательное поле",
                validate: (value) => {
                  if (password !== value) {
                    return "Пароли не совпадают";
                  }
                },
              })}
              size="xl"
              type="password"
              validationState={errors.password_repeat ? "invalid" : undefined}
              errorMessage={
                password !== watch("password_repeat")
                  ? "Пароли не совпадают"
                  : undefined
              }
              errorPlacement="outside"
            />
          </FormField>
          <Flex
            className={css.submitGroup}
            direction={"column"}
            alignItems={"center"}
            gap={"3"}
          >
            <Button
              className={css.regButton}
              view="action"
              size="l"
              type="submit"
              loading={isLoading}
              width="max"
            >
              Зарегистрироваться
            </Button>
            <div
              className={css.linkLabel}
              onClick={() =>
                navigate(forwardRedirectUrl(RoutesPaths.AUTH.LOGIN))
              }
            >
              У меня уже есть аккаунт
            </div>
          </Flex>
        </Form>
      </Flex>
    </Flex>
  );
};
